// Right-rail panels for the hub feed — UPCOMING (events), LATEST PROJECTS,
// and TOP CONTRIBUTORS. Markup mirrors design/project/scripts/feed.jsx's
// rail widgets (EventWidget / ProjectsPeekWidget / ContributorsWidget) and
// uses the design's verbatim `.widget` / `.evt-*` / `.peekrow` / `.contrib`
// classes from index.css.
//
// Each panel wires to a live endpoint via the existing feature api modules:
//   UPCOMING          -> GET /api/v1/events       (directories/EventsCalendar)
//   LATEST PROJECTS   -> GET /api/v1/projects      (directories/ProjectsPage)
//   TOP CONTRIBUTORS  -> GET /api/v1/leaderboard   (reputation-leaderboard)
import { useEffect, useState } from "react";
import { apiClient } from "@/lib/api-client";
import type { AvatarConfig } from "@/features/avatar/catalog";
import { Avatar, Icon, timeAgo } from "../design-atoms";
import { downloadICS } from "../calendar";

// ---------------------------------------------------------------------------
// Shared response shapes (mirror the api schemas; not hand-edited api-types.ts)
// ---------------------------------------------------------------------------

interface EventItem {
  id: string;
  title: string;
  tone: string;
  cadence: string;
  time_start: string;
  time_end: string;
  recurrence_dates: string[];
  location?: string | null;
  note?: string | null;
}

interface EventListResponse {
  items: EventItem[];
}

interface ProjectItem {
  id: string;
  title: string;
  project_type: string | null;
  vote_count: number;
  created_at: string;
}

interface ProjectsListResponse {
  items: ProjectItem[];
}

interface LeaderboardEntry {
  rank: number;
  member_id: string;
  name: string;
  initials: string;
  division: string;
  role: string;
  reputation: number;
  avatar?: AvatarConfig | null;
}

interface LeaderboardResponse {
  entries: LeaderboardEntry[];
}

// ---------------------------------------------------------------------------
// Date helpers
// ---------------------------------------------------------------------------

const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MON = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

function formatEventDate(dateStr: string): string {
  const [y, m, d] = dateStr.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  return `${DOW[dt.getDay()]}, ${MON[dt.getMonth()]} ${dt.getDate()}`;
}

// Local "today" as YYYY-MM-DD (recurrence dates are YYYY-MM-DD, which sort
// lexicographically, so a string >= compare is a correct date compare).
function todayISO(): string {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
}

// The next occurrence on or after today — the recurrence_dates are sorted
// ascending and include past dates, so [0] is the earliest ever, not the next
// upcoming one. Returns undefined when every occurrence is in the past.
function nextOccurrence(dates: string[]): string | undefined {
  const today = todayISO();
  return dates.find((d) => d >= today);
}

// ---------------------------------------------------------------------------
// UPCOMING — events widget
// ---------------------------------------------------------------------------

interface EventsRailProps {
  onOpenCalendar?: () => void;
}

export function EventsRail({ onOpenCalendar }: EventsRailProps) {
  const [items, setItems] = useState<EventItem[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiClient<EventListResponse>("/api/v1/events")
      .then((res) => !cancelled && setItems(res.items))
      .catch(() => !cancelled && setItems([]));
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section className="widget" aria-label="Events">
      <div className="widget-h">
        <span className="wt">Events</span>
        <button type="button" className="seeall" onClick={onOpenCalendar}>
          <Icon.calendar size={12} /> Open calendar
        </button>
      </div>
      <div className="widget-b">
        {items === null ? (
          <div className="rail-empty">Loading events…</div>
        ) : items.length === 0 ? (
          <div className="rail-empty">No sessions scheduled yet.</div>
        ) : (
          <div className="evt-list">
            {items.map((e) => {
              const next = nextOccurrence(e.recurrence_dates);
              return (
                <div className="evt-row" key={e.id}>
                  <span className={`evt-dot ${e.tone === "blue" ? "blue" : "green"}`} />
                  <div style={{ minWidth: 0 }}>
                    <div className="evt-title">{e.title}</div>
                    <div className="evt-meta">
                      {next ? formatEventDate(next) : e.cadence} · {e.time_start}–{e.time_end}
                    </div>
                    {next && (
                      <button
                        type="button"
                        className="evt-addcal"
                        onClick={() =>
                          downloadICS({
                            title: e.title,
                            date: next,
                            time_start: e.time_start,
                            time_end: e.time_end,
                            location: e.location,
                            note: e.note,
                          })
                        }
                      >
                        <Icon.calendar size={11} /> Add to calendar
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// LATEST PROJECTS — peek widget
// ---------------------------------------------------------------------------

const PROJECT_TYPE_COLOR: Record<string, string> = {
  Skill: "var(--nfs, #0f7d87)",
  "MCP Server": "var(--ss, #bd1f6a)",
  Plugin: "#4d7c0f",
  Automation: "var(--nds, #5b2d8e)",
  Application: "var(--nbs, #1a6fa3)",
};

interface ProjectsRailProps {
  onSeeAll?: () => void;
  onOpen?: (id: string) => void;
}

export function ProjectsRail({ onSeeAll, onOpen }: ProjectsRailProps) {
  const [items, setItems] = useState<ProjectItem[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiClient<ProjectsListResponse>("/api/v1/projects")
      .then((res) => !cancelled && setItems(res.items))
      .catch(() => !cancelled && setItems([]));
    return () => {
      cancelled = true;
    };
  }, []);

  const top = items?.slice(0, 4) ?? null;

  return (
    <section className="widget" aria-label="Latest projects">
      <div className="widget-h">
        <span className="wt">Latest Projects</span>
        <button type="button" className="seeall" onClick={onSeeAll}>
          See all <Icon.arrowR size={12} />
        </button>
      </div>
      <div className="widget-b">
        {top === null ? (
          <div className="rail-empty">Loading projects…</div>
        ) : top.length === 0 ? (
          <div className="rail-empty">No projects shared yet.</div>
        ) : (
          top.map((p) => (
            <div
              className="peekrow"
              key={p.id}
              role="button"
              tabIndex={0}
              onClick={() => onOpen?.(p.id)}
              onKeyDown={(ev) => {
                if (ev.key === "Enter" || ev.key === " ") {
                  ev.preventDefault();
                  onOpen?.(p.id);
                }
              }}
            >
              <div className="peek-body">
                <div className="peek-title">{p.title}</div>
                <div className="peek-meta">
                  {p.project_type && (
                    <span
                      className="dtag"
                      style={{
                        color: PROJECT_TYPE_COLOR[p.project_type] ?? "var(--ink-2)",
                        background: "var(--surface-2)",
                      }}
                    >
                      {p.project_type}
                    </span>
                  )}
                  <span>
                    <Icon.up size={11} /> {p.vote_count}
                  </span>
                  <span>{timeAgo(p.created_at)}</span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// TOP CONTRIBUTORS — leaderboard widget
// ---------------------------------------------------------------------------

export function ContributorsRail() {
  const [entries, setEntries] = useState<LeaderboardEntry[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiClient<LeaderboardResponse>("/api/v1/leaderboard")
      .then((res) => !cancelled && setEntries(res.entries))
      .catch(() => !cancelled && setEntries([]));
    return () => {
      cancelled = true;
    };
  }, []);

  const top = entries?.slice(0, 5) ?? null;

  return (
    <section className="widget" aria-label="Top contributors">
      <div className="widget-h">
        <span className="wt">Top Contributors</span>
      </div>
      <div className="widget-b">
        {top === null ? (
          <div className="rail-empty">Loading contributors…</div>
        ) : top.length === 0 ? (
          <div className="rail-empty">No contributors yet — be the first to share.</div>
        ) : (
          top.map((e) => (
            <div className="contrib" key={e.member_id}>
              <span className="rk">{e.rank}</span>
              <Avatar id={e.member_id} initials={e.initials} name={e.name} avatar={e.avatar} size={32} />
              <div className="ci">
                <b>{e.name}</b>
              </div>
              <span className="rep">{e.reputation}</span>
            </div>
          ))
        )}
      </div>
    </section>
  );
}
